<template>
  <div class="download-page">
    <div class="download-content">
      <header class="download-header">
        <h1>Bulk Download</h1>
        <p>Download complete association and mediation results from MetabHub.</p>
        <p>Files are provided as compressed <strong>.tar.gz</strong> archives.</p>
      </header>

      <section v-for="group in downloadGroups" :key="group.key" class="download-group">
        <div class="group-header">
          <span class="group-icon" aria-hidden="true">
            <svg v-if="group.key === 'environment'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M19 4C10 4 5 8 5 15c0 2.5 1.7 4 4 4 4.5 0 8-5.5 10-15Z" />
              <path d="M5 19c2.5-4 5.5-6.5 10-8" />
            </svg>
            <svg v-else-if="group.key === 'clinical'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M8 3v5a4 4 0 0 0 8 0V3" />
              <path d="M8 6H5a2 2 0 0 0-2 2v1a6 6 0 0 0 6 6h2" />
              <path d="M16 6h3a2 2 0 0 1 2 2v1a6 6 0 0 1-6 6h-2" />
              <path d="M12 15v6M9 21h6" />
            </svg>
            <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M8 3c4 2 4 6 0 9s-4 7 0 9" />
              <path d="M16 3c-4 2-4 6 0 9s4 7 0 9" />
              <path d="M9 6h6M8 12h8M9 18h6" />
            </svg>
          </span>
          <h2>{{ group.title }}</h2>
        </div>

        <div class="download-table-head">
          <span>File</span>
          <span>Description</span>
          <span>Format</span>
          <span aria-hidden="true"></span>
        </div>
        <div v-for="item in group.items" :key="item.key" class="download-row">
          <div class="file-code">{{ item.code }}</div>
          <div class="file-description">
            <strong>{{ item.title }}</strong>
            <span>{{ item.description }}</span>
          </div>
          <div class="file-format">.tar.gz</div>
          <a
            class="download-action"
            :href="downloadUrl(item)"
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M12 3v12" />
              <path d="m7 10 5 5 5-5" />
              <path d="M5 21h14" />
            </svg>
            <span>Download</span>
          </a>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import request from '@/utils/request'

export default {
  name: 'BulkDownload',
  data() {
    return {
      downloadGroups: [
        {
          key: 'environment',
          title: 'Environmental Exposure',
          items: [
            { key: 'e-d', code: 'E-D', title: 'Environmental exposure - disease associations', description: 'Association results between environmental exposures and diseases.', filename: 'E-D.tar.gz', url: '/api/download/E-D.tar.gz' },
            { key: 'e-m', code: 'E-M', title: 'Environmental exposure - metabolite associations', description: 'Association results between environmental exposures and metabolites.', filename: 'E-M.tar.gz', url: '/api/download/E-M.tar.gz' },
            { key: 'e-m-d', code: 'E-M-D', title: 'Environmental exposure - metabolite - disease mediation', description: 'Metabolite-mediated pathways linking environmental exposures to diseases.', filename: 'E-M-D.tar.gz', url: '/api/download/E-M-D.tar.gz' }
          ]
        },
        {
          key: 'clinical',
          title: 'Clinical Measurement',
          items: [
            { key: 'm-d', code: 'M-D', title: 'Clinical measurement - disease associations', description: 'Association results between clinical measurements and diseases.', filename: 'M-D.tar.gz', url: '/api/download/M-D.tar.gz' },
            { key: 'm-m', code: 'M-M', title: 'Clinical measurement - metabolite associations', description: 'Association results between clinical measurements and metabolites.', filename: 'M-M.tar.gz', url: '/api/download/M-M.tar.gz' },
            { key: 'm-m-d', code: 'M-M-D', title: 'Clinical measurement - metabolite - disease mediation', description: 'Metabolite-mediated pathways linking clinical measurements to diseases.', filename: 'M-M-D.tar.gz', url: '/api/download/M-M-D.tar.gz' }
          ]
        },
        {
          key: 'genotype',
          title: 'Genotype',
          items: [
            { key: 'g-d', code: 'G-D', title: 'Genotype - disease associations', description: 'Association results between genetic variants and diseases.', filename: 'G-D.tar.gz', url: '/api/download/G-D.tar.gz' },
            { key: 'g-m', code: 'G-M', title: 'Genotype - metabolite associations', description: 'Association results between genetic variants and metabolites.', filename: 'G-M.tar.gz', url: '/api/download/G-M.tar.gz' },
            { key: 'g-m-d', code: 'G-M-D', title: 'Genotype - metabolite - disease mediation', description: 'Metabolite-mediated pathways linking genetic variants to diseases.', filename: 'G-M-D.tar.gz', url: '/api/download/G-M-D.tar.gz' }
          ]
        }
      ]
    }
  },
  methods: {
    downloadUrl(item) {
      return `${request.defaults.baseURL || ''}${item.url}`
    }
  }
}
</script>

<style lang="less" scoped>
.download-page {
  min-height: 100%;
  background: #fff;
  color: #172033;
}

.download-content {
  max-width: 1180px;
  margin: 0 auto;
  padding: 34px 32px 56px;
}

.download-header {
  margin-bottom: 26px;
}

.download-header h1 {
  margin: 0 0 8px;
  color: #111827;
  font-size: 32px;
  line-height: 1.15;
  font-weight: 800;
}

.download-header p {
  margin: 3px 0;
  color: #65738b;
  font-size: 16px;
  line-height: 1.45;
}

.download-header strong {
  color: #475569;
}

.download-group {
  margin: 0 0 22px;
  overflow: hidden;
  border: 1px solid #fecdd3;
  border-radius: 9px;
  background: #fff;
  box-shadow: 0 4px 16px rgba(190, 24, 93, 0.06);
}

.group-header {
  display: flex;
  align-items: center;
  gap: 15px;
  min-height: 60px;
  padding: 0 18px;
  background: linear-gradient(90deg, #fff1f2, #fff7f8);
}

.group-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  border-radius: 50%;
  background: #e11d48;
  color: #fff;
}

.group-icon svg {
  width: 23px;
  height: 23px;
}

.group-header h2 {
  margin: 0;
  color: #1f2937;
  font-size: 20px;
  line-height: 1.2;
  font-weight: 750;
}

.download-table-head,
.download-row {
  display: grid;
  grid-template-columns: 105px minmax(0, 1fr) 90px 142px;
  column-gap: 18px;
  align-items: center;
  padding: 0 20px;
}

.download-table-head {
  min-height: 34px;
  background: #fff9fa;
  color: #9f5364;
  font-size: 12px;
  font-weight: 700;
}

.download-row {
  min-height: 66px;
  border-top: 1px solid #f1f5f9;
}

.file-code {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  border-radius: 7px;
  background: #fff0f2;
  color: #d51d3c;
  font-size: 14px;
  font-weight: 800;
}

.file-description {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.file-description strong {
  overflow: hidden;
  color: #334155;
  font-size: 14px;
  line-height: 1.3;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-description span {
  overflow: hidden;
  color: #8491a5;
  font-size: 12px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-format {
  color: #475569;
  font-size: 13px;
  font-weight: 600;
}

.download-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 36px;
  padding: 0 16px;
  border-radius: 6px;
  background: #e11d2e;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  transition: background 0.18s ease, transform 0.18s ease;
}

.download-action:hover {
  background: #be123c;
  transform: translateY(-1px);
}

.download-action svg {
  width: 17px;
  height: 17px;
}

@media (max-width: 760px) {
  .download-content {
    padding: 26px 16px 40px;
  }

  .download-header h1 {
    font-size: 28px;
  }

  .download-table-head {
    display: none;
  }

  .download-row {
    grid-template-columns: 72px minmax(0, 1fr);
    gap: 10px 12px;
    padding: 14px 16px;
  }

  .file-format {
    grid-column: 1;
    grid-row: 2;
  }

  .download-action {
    grid-column: 2;
    grid-row: 2;
    justify-self: start;
    min-height: 32px;
  }

  .file-description strong,
  .file-description span {
    white-space: normal;
  }
}
</style>
